import { faBarsFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Page from 'components/layout/Page';
import { Button } from 'components/shared/Button';
import Notification from 'components/shared/Notification';
import Spinner from 'components/shared/Spinner';
import Filters from './filters/Filters';
import {
  useJobOffersAlerts,
  useJobOffersList
} from './JobOffersListComponent.hooks';
import {
  StyledFiltersContainer,
  StyledFiltersGrid,
  StyledFilterSort,
  StyledJobOfferListComponent,
  StyledJobOfferListTitle
} from './JobOffersListComponent.styled';
import { JobOffersListComponentProps } from './JobOffersListComponent.types';

import dynamic from 'next/dynamic';

const OfferGrid = dynamic(() => import('./grid/OfferGrid'), { ssr: false });
const OfferListEmpty = dynamic(() => import('./empty/JobOffersListEmpty'), {
  ssr: false
});

const OfferAlertConfirmSaveModal = dynamic(
  () => import('./alerts/OfferAlertConfirmSaveModal'),
  { ssr: false }
);
const OfferAlertModal = dynamic(() => import('./alerts/OfferAlertModal'), {
  ssr: false
});

const JobOffersListComponent: React.FC<JobOffersListComponentProps> = ({
  currencies,
  languages,
  techs,
  techRoles,
  allLanguages
}) => {
  const {
    t,
    UIOffers,
    currentFilters,
    currentFiltersText,
    loading,
    activeFilters,
    setActiveFilters,
    filtersDesktop,
    isDefaultFilter,
    handleOnFilterChange,
    handleOnFilterReset
  } = useJobOffersList({
    languages,
    allLanguages,
    techs
  });

  const {
    alerts,
    setAlerts,
    isManageAlertsModalOpened,
    setIsManageAlertsModalOpened,
    unsuscribeActionOfferAlert,
    handleOpenManageAlertsClick,
    isConfirmSaveModalOpened,
    setIsConfirmSaveModalOpened,
    isSavingAlert,
    handleCreateAlertClick,
    handleSignupClick,
    userCanCreateAlerts
  } = useJobOffersAlerts({
    currentFilters
  });

  return (
    <Page
      hasScrollTop
      filters={
        !filtersDesktop && (
          <Filters
            onFilterChange={handleOnFilterChange}
            onFilterReset={handleOnFilterReset}
            onClose={() => {
              setActiveFilters(false);
            }}
            onManageAlertsClick={handleOpenManageAlertsClick}
            onCreateAlertClick={() => setIsConfirmSaveModalOpened(true)}
            isDefaultFilter={isDefaultFilter}
            currentFilter={currentFilters}
            currencies={currencies}
            languages={languages}
            techs={techs}
            techRoles={techRoles}
            allLanguages={allLanguages}
            className={classNames({
              showFilters: activeFilters
            })}
            userHasOfferAlerts={alerts && alerts.length > 0}
            isSavingOfferAlert={isSavingAlert}
          />
        )
      }
    >
      <StyledJobOfferListComponent>
        <StyledJobOfferListTitle editorial>
          {t('page_title')}
        </StyledJobOfferListTitle>

        <OfferAlertModal
          isOpened={isManageAlertsModalOpened}
          close={() => setIsManageAlertsModalOpened(false)}
          offerAlerts={alerts}
          techs={techs}
          techRoles={techRoles}
          setOfferAlerts={setAlerts}
          userCanCreateAlerts={userCanCreateAlerts}
          unsuscribeActionOfferAlert={unsuscribeActionOfferAlert}
          onCreateAlertClick={() => {
            setIsManageAlertsModalOpened(false);

            if (!isDefaultFilter) {
              setIsConfirmSaveModalOpened(true);
            }
          }}
        />

        <OfferAlertConfirmSaveModal
          isOpen={isConfirmSaveModalOpened}
          close={() => setIsConfirmSaveModalOpened(false)}
          onConfirm={handleCreateAlertClick}
          onSignupClick={handleSignupClick}
        />

        <StyledFilterSort>
          <Button
            sizes="sm"
            variant="tertiary"
            onClick={() => setActiveFilters(true)}
            className="filters-trigger"
          >
            <FontAwesomeIcon icon={faBarsFilter} />
            <span>{t('filters_title')}</span>
          </Button>
        </StyledFilterSort>

        {filtersDesktop && currentFilters && (
          <StyledFiltersContainer>
            <Filters
              onFilterChange={handleOnFilterChange}
              onFilterReset={handleOnFilterReset}
              onCreateAlertClick={() => setIsConfirmSaveModalOpened(true)}
              onManageAlertsClick={handleOpenManageAlertsClick}
              isDefaultFilter={isDefaultFilter}
              currentFilter={currentFilters}
              currencies={currencies}
              languages={languages}
              techs={techs}
              techRoles={techRoles}
              allLanguages={allLanguages}
              userHasOfferAlerts={alerts && alerts.length > 0}
              isSavingOfferAlert={isSavingAlert}
            />
          </StyledFiltersContainer>
        )}

        <StyledFiltersGrid>
          {!filtersDesktop && currentFiltersText.length > 0 && (
            <Notification variant="info">
              <p>
                {t('applied_filters', { count: currentFiltersText.length })}
              </p>
              <span>
                {currentFiltersText.map((f, i) => (
                  <span key={i}>
                    <strong>{f}</strong>
                    {i < currentFiltersText.length - 1 && <>, </>}
                  </span>
                ))}
              </span>
            </Notification>
          )}

          {loading ? (
            <Spinner />
          ) : (
            <>
              {UIOffers?.length > 0 ? (
                <OfferGrid
                  isDefaultFilter={isDefaultFilter}
                  offers={UIOffers}
                  className={classNames({
                    loading: loading
                  })}
                />
              ) : (
                <OfferListEmpty
                  handleCreateAlertClick={() =>
                    setIsConfirmSaveModalOpened(true)
                  }
                  isSavingOfferAlert={isSavingAlert}
                />
              )}
            </>
          )}
        </StyledFiltersGrid>
      </StyledJobOfferListComponent>
    </Page>
  );
};

export default JobOffersListComponent;
